import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { PayType, apiError, type CreateCompensationInput, type EmployeeCompensation } from './lib';

/**
 * Pay history for one employee. `compensation.view` reached this component at
 * all; `compensation.manage` is checked again, separately, before the "record
 * a rise" form is offered — the same read/write split every other module in
 * this dashboard keeps. Effective-dated and append-only: recording a rise
 * writes a new row rather than editing the old rate, so a March export still
 * re-derives March's number.
 */
export default function CompensationPanel({ employeeId }: { employeeId: string }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canManage = can('compensation.manage');

  const [rows, setRows] = useState<EmployeeCompensation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<EmployeeCompensation[]>(`/employees/${employeeId}/compensation`)
      .then(({ data }) => setRows(data))
      .catch((e) => setError(apiError(e, t('workforce.compensation.loadFailed'))))
      .finally(() => setLoading(false));
  }, [employeeId, t]);

  useEffect(load, [load]);

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="space-y-3">
      {canManage && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => setFormOpen(true)}>
            {t('workforce.compensation.recordRise')}
          </Button>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState title={t('workforce.compensation.empty')} hint={t('workforce.compensation.emptyHint')} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('workforce.compensation.payType')}</TH>
                <TH className="text-end">{t('workforce.compensation.rate')}</TH>
                <TH>{t('workforce.compensation.effectiveFrom')}</TH>
                <TH>{t('workforce.compensation.effectiveTo')}</TH>
              </TR>
            </THead>
            <tbody>
              {rows.map((row) => (
                <TR key={row.id}>
                  <TD>{t(`workforce.payType.${row.payType}`)}</TD>
                  {/* The server's own decimal string, and the record's OWN
                      currency — never the tenant default, and never parsed. */}
                  <TD className="text-end tabular-nums">
                    {row.currency.symbol} {row.baseRate}
                  </TD>
                  <TD>{formatDateTime(row.effectiveFrom, i18n.language)}</TD>
                  <TD className="text-ink-muted">
                    {row.effectiveTo ? formatDateTime(row.effectiveTo, i18n.language) : t('workforce.compensation.current')}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {formOpen && (
        <CompensationFormDialog
          employeeId={employeeId}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function CompensationFormDialog({
  employeeId,
  onClose,
  onSaved,
}: {
  employeeId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [payType, setPayType] = useState<PayType>(PayType.HOURLY);
  const [baseRate, setBaseRate] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!baseRate.trim() || !currencyCode.trim()) return;
    setSaving(true);
    try {
      const body: CreateCompensationInput = {
        payType,
        baseRate: baseRate.trim(),
        currencyCode: currencyCode.trim().toUpperCase(),
        effectiveFrom,
      };
      await api.post(`/employees/${employeeId}/compensation`, body);
      toast.success(t('workforce.compensation.saved'));
      onSaved();
    } catch (e) {
      toast.error(apiError(e, t('workforce.compensation.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('workforce.compensation.recordRise')}
      description={t('workforce.compensation.recordRiseHint')}
      className="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={saving || !baseRate.trim() || !currencyCode.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('workforce.compensation.payType')}>
          <Select value={payType} onChange={(e) => setPayType(e.target.value as PayType)}>
            {Object.values(PayType).map((p) => (
              <option key={p} value={p}>
                {t(`workforce.payType.${p}`)}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t('workforce.compensation.rate')} hint={t('workforce.compensation.rateHint')}>
            <Input inputMode="decimal" placeholder="45.0000" value={baseRate} onChange={(e) => setBaseRate(e.target.value)} />
          </Field>
          <Field label={t('workforce.compensation.currency')}>
            <Input placeholder="SAR" maxLength={3} value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)} />
          </Field>
        </div>
        <Field label={t('workforce.compensation.effectiveFrom')} hint={t('workforce.compensation.effectiveFromHint')}>
          <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
